import { z } from "zod";
import { throwIfError } from "@/lib/api/parseResponse";
import { clientFetch } from "@/lib/api/client";
import { proxyPath } from "@/lib/api/paths";

const geographyOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  coming_soon: z.boolean().optional(),
});

const geographyOptionsSchema = z.object({
  countries: z.array(geographyOptionSchema),
  areas_by_country: z.record(z.string(), z.array(geographyOptionSchema)),
});

export type GeographyOption = z.infer<typeof geographyOptionSchema>;
export type GeographyOptions = z.infer<typeof geographyOptionsSchema>;

export async function fetchGeographyOptions(opts?: {
  signal?: AbortSignal;
}): Promise<GeographyOptions> {
  const { res, body } = await clientFetch(
    proxyPath("v1/questionnaires/geography-options"),
    { method: "GET", signal: opts?.signal },
  );

  throwIfError(res, body);
  // Static reference data — a malformed body means something is seriously
  // wrong upstream, so this throws rather than degrading silently. The
  // caller (useQuizWizardController) already treats any rejection here as
  // geoError and falls back to humanized labels.
  return geographyOptionsSchema.parse(body);
}

// Fallback display for a country/region value whose label couldn't be resolved
// (options never loaded, or the value predates the current options list).
// e.g. "greater_kruger" -> "Greater Kruger".
export function humanizeGeoValue(value: string): string {
  return value
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
